/*
This dialog walks users through setting up an account and integration. It goes:

  1. Choose a product
  2. Choose an integration
  3. Create a (user) account
  4. Terms of service
  5. Create an organization
  6. Enter/request/generate API keys.
  7. (send email to dutchie and generate link back and add them to the reminder queue or generate keys on metrc)
  8. (test credentials)
  9. If metrc; set up quotas
  10. (Send payment intent to stripe)
  11. (User checks out on stripe)
  12. (Receive webhook from stripe)
  13. Redirect to backfills page
  14. (Refresh backfills page with updated status)

 */
import React, {useRef, useEffect, ReactNode, useState} from "react";
import {Tracker} from "weed-js";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import {
    ANALYTICS_MONTHLY,
    STEPS,
    TOS,
    REGISTER,
    CREATE_AN_ORGANIZATION,
    CHECKOUT,
    CHOOSE_A_PRODUCT,
    CHOOSE_AN_INTEGRATION,
    SET_UP_KEYS,
} from "./constants";
import "./style.css";
import Checkout from "./Checkout";
import ChooseAnIntegration from "./ChooseAnIntegration";
import ChooseAProduct from "./ChooseAProduct";
import CreateAnOrganization from "./CreateAnOrganization";
import Register from "./Register";
import SetUpKeys from "./SetUpKeys";
import {CreatingAccountSpinner} from "./CreatingAccount";
import {TOS as TOSComponent} from "./TOS";
import {getMetrcStripeIds, METRC, DUTCHIE, getDutchieStripeIds} from "../../../constants";
import {createUser, enqueueSnackbar} from "../../../redux/Actions";
import {useDispatch} from "react-redux";
import {
    fetchOrganizations,
    fetchOrganizationUserGroupsForUser,
    fetchOrganizationUsers,
    fetchUserProfile,
    login,
    createOrganization, updateUserProfile
} from "../../../redux/user/Action.js";
import {
    fetchLeaflogixAccounts,
    fetchMetrcAccounts,
    selectIntegrationAccounts
} from "../../../redux/models/integrations/Action";
import {isEmpty} from "weed-js";
import {userLoggedIn} from "../../../redux/user/Action";
import {selectOrganization} from "../../../redux/models/organizations/Action";
import {useSearchParams} from "react-router-dom";


const formIncomplete = (loggedInUser, formState, activeStep, integrationAccounts) => {
    if (activeStep === 0) {
        return !formState.product;
    } else if (activeStep === 1) {
        return !formState.integration;
    } else if (activeStep === 2) {
        return !formState.email; // || !formState.first_name || !formState.last_name || !formState.password || !formState.confirm_password;
    } else if (activeStep === 3) {
        return !formState.organization_name;
    } else if (activeStep === 4) {
        return !formState.accepted_tos;
    } else if (activeStep === 6) {
        if (loggedInUser?.is_superuser) {
            return false;
        }
        if (!formState.api_key) {
            return false;
        }
        if (!formState.confirm_api_key) {
            return false;
        }
        if (formState.integration === METRC && !formState.state_id) {
            return false;
        }
        if (formState.integration === DUTCHIE && !formState.location_name) {
            return false;
        }
    } else if (activeStep === STEPS.indexOf(SET_UP_KEYS)) {
        return isEmpty(integrationAccounts);
    }

    return false;
};

const validateRegister = (loggedInUser, userProfile, formState, setErrors, errors, defaultErrors) => {
    if (loggedInUser && userProfile) {
        if (!userProfile.email_is_verified) {
            setErrors({
                ...errors, ...defaultErrors,
                email: "Email is not verified. Please check your email for a verification link."
            });
            return false;
        }
    }
    const newErrors = {...defaultErrors};
    if (!formState.email) {
        newErrors.email = "Email is required.";
    }
    // if (!formState.first_name) {
    //     newErrors.first_name = "First name is required.";
    // }
    // if (!formState.last_name) {
    //     newErrors.last_name = "Last name is required.";
    // }
    // if (!formState.password) {
    //     newErrors.password = "Password is required.";
    // } else if (formState.password.length < 10) {
    //     newErrors.password = "Password must be at least 10 characters long. And have at least one special character (not alphanumeric).";
    // } else if (formState.password.search(/[^a-zA-Z0-9]/) === -1) {
    //     newErrors.password = "Password must be at least 10 characters long. And have at least one special character (not alphanumeric).";
    // }
    // if (!formState.confirm_password) {
    //     newErrors.confirm_password = "Please confirm your password.";
    // }
    // if (formState.password !== formState.confirm_password) {
    //     newErrors.confirm_password += " Passwords don't match.";
    // }

    setErrors(newErrors);
    return Object.values(newErrors).every(error => error === "");
};

const validateOrganization = (formState, setErrors, errors) => {
    if (!formState.organization_name) {
        setErrors({
            ...errors,
            organization_name: "Organization name is required."
        });
        return false;
    }
    return true;
};


const useResponsiveOrientation = () => {

    const slimScreen = () => {
        return window.innerWidth < 900;
    };

    const shortScreen = () => {
        return window.innerHeight < 800;
    };

    const isMobile = () => {
        return window.innerWidth <= 768 || window.innerHeight <= 768;
    }

    const getOrientation = () => ({
        isVertical: !(slimScreen() || shortScreen()),
        isShort: shortScreen(),
        isMobile: isMobile(),
        isSlim: slimScreen(),
    });

    const [orientation, setOrientation] = useState(getOrientation());

    useEffect(() => {
        const handleResize = () => {
            setOrientation(getOrientation());
        };

        window.addEventListener("resize", handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return orientation;
}

export const CreatePaidAccountStepper = ({activeStep, orientation}) => {
    return <Stepper activeStep={activeStep} orientation={orientation} className={"create-paid-account-stepper" + (orientation === "vertical" ? " vertical" : " horizontal")}>
        {STEPS.map((label, index) => (
            <Step key={label} completed={activeStep > index}>
                <StepLabel>{label}</StepLabel>
            </Step>
        ))}
    </Stepper>;
};

export const StepComponent = ({...props}) => {
    const activeStep = props.activeStep;
    try {
        Tracker.trackSignupStep({stepName: STEPS?.[activeStep], stepIndex: activeStep});
    } catch (e) {}
    if (STEPS[activeStep] === CHOOSE_A_PRODUCT) {
        return <ChooseAProduct {...props}/>;
    } else if (STEPS[activeStep] === CHOOSE_AN_INTEGRATION) {
        return <ChooseAnIntegration {...props}/>;
    } else if (STEPS[activeStep] === REGISTER) {
        return <Register {...props}/>;
    } else if (STEPS[activeStep] === TOS) {
        return <TOSComponent {...props} />;
    } else if (STEPS[activeStep] === CREATE_AN_ORGANIZATION) {
        return <CreateAnOrganization {...props} />;
    } else if (STEPS[activeStep] === SET_UP_KEYS) {
        return <SetUpKeys {...props}/>;
    } else if (STEPS[activeStep] === CHECKOUT) {
        return <Checkout {...props}/>;
    } else {
        return <div>Unknown step: {STEPS[activeStep]}</div>;
    }
};

const fetchUserMetadata = ({user, onSend, onComplete, onError, dispatch}) => {
    onSend();
    return Promise.all([
        dispatch(fetchUserProfile(user)),
        dispatch(fetchOrganizationUserGroupsForUser(user)),
        dispatch(fetchOrganizationUsers(null, user.id)),
        dispatch(fetchOrganizations(null)),
    ]).then(
        ([
            fetchedUserProfile,
            fetchedOrganizationUserGroups,
            fetchedOrganizationUsers,
            fetchedOrganizations
        ]) => {
            if (!isEmpty(fetchedOrganizations)) {
                return Promise.all([
                    dispatch(fetchMetrcAccounts()),
                    dispatch(fetchLeaflogixAccounts()),
                ]).then(([fetchedMetrcAccounts, fetchedDutchieAccounts]) => {
                    const integrationAccounts = [
                        ...(isEmpty(fetchedMetrcAccounts) ? [] : fetchedMetrcAccounts),
                        ...(isEmpty(fetchedDutchieAccounts) ? [] : fetchedDutchieAccounts)
                    ];
                    return onComplete(
                        user,
                        fetchedUserProfile,
                        fetchedOrganizationUsers,
                        fetchedOrganizations,
                        fetchedOrganizationUserGroups,
                        integrationAccounts
                    );
                });
            }
            return onComplete(user, fetchedUserProfile, fetchedOrganizationUsers, fetchedOrganizations, fetchedOrganizationUserGroups, []);
        }).catch((err1) => {
        onError();
        console.error("Got errors (1)", err1);
    });
};

const getCompletedSteps = ({
    loggedInUser,
    userProfile,
    formState,
    selectedIntegrationAccounts
}) => {
    const completedSteps = [];
    if (formState?.store_count > 0) {
        completedSteps.push(CHOOSE_A_PRODUCT);
    }
    if (formState?.integration) {
        completedSteps.push(CHOOSE_AN_INTEGRATION);
    }
    if (loggedInUser?.id && userProfile?.email_is_verified) {
        completedSteps.push(REGISTER);
    }
    if (formState.accepted_tos) {
        completedSteps.push(TOS);
    }
    if (formState.organization_id) {
        completedSteps.push(CREATE_AN_ORGANIZATION);
    }
    if (!isEmpty(selectedIntegrationAccounts)) {
        completedSteps.push(SET_UP_KEYS);
    }
    return completedSteps;
};

export const CreatePaidAccount = ({
    hideModal,
    setHideModal,
    loggedInUser,
    userProfile,
    setLoggedInUser,
    setUserProfile,
    organizations,
    setOrganizations,
    selectedOrganization,
    selectedIntegration,
    selectedIntegrationAccounts
}) => {

    const [activeStep, setActiveStep] = useState(0);
    const [allowBackNavigation, setAllowBackNavigation] = useState(false);
    const [userOverride, setUserOverride] = useState(false);

    const [params, setParams] = useSearchParams();
    // Variables should first come from parameters, because those are set by linkbacks.
    // Then they should come from the formState, because those are set by the user. Default form state is set from
    // selected objects.
    // Last they should come from the selected objects (org, user, integration, etc)


    const defaultState = useRef({
        accepted_tos: isEmpty(userProfile) ? false : userProfile.has_accepted_tos, // true,
        api_key: "",
        confirm_api_key: "",
        email: isEmpty(loggedInUser) ? "" : loggedInUser.email, // Math.random() + "@bar.com",
        integration: params.get("integration") ? params.get("integration") : selectedIntegration, // "metrc", // dutchie, leaflogix
        location_name: params.get("location_name") || "",
        organization_id: params.get("organization_id") || selectedOrganization?.id || "", // "org_12345",
        organization_name: params.get("organization_name") || isEmpty(selectedOrganization) ? "" : selectedOrganization.name, // "My Organization",
        product: params.get("product") ? params.get("product") : ANALYTICS_MONTHLY,
        state_id: params.get("state_id") || "",
        store_count: params.get("store_count") ? parseInt(params.get("store_count")) : 0,
        stripe_price_external_id: params.get("stripe_price_external_id") || "",
        stripe_product_external_id: params.get("stripe_product_external_id") || "",
    });

    const [formState, setFormState] = useState(defaultState.current);

    console.log("Organization ID:", formState.organization_id, "Name:", formState.organization_name, "Selected org:", selectedOrganization);

    const defaultErrors = useRef({
        accepted_tos: "",
        api_key: "",
        confirm_api_key: "",
        email: "",
        integration: "",
        location_name: "",
        organization_id: "",
        organization_name: "",
        product: "",
        state_id: "",
        store_count: "",
        stripe_price_external_id: "",
        stripe_product_external_id: "",
    });

    const [errors, setErrors] = useState(defaultErrors.current);
    const [creatingAccount, setCreatingAccount] = useState(false);
    const dispatch = useDispatch();
    const [creatingUser, setCreatingUser] = useState(false);
    const [creatingOrganization, setCreatingOrganization] = useState(false);
    const [configuringUsers, setConfiguringUsers] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);
    const [creatingIntegration, setCreatingIntegration] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [integrationAccounts, setIntegrationAccounts] = useState([]);
    const {isVertical, isMobile} = useResponsiveOrientation();
    const [verifyLink, setVerifyLink] = useState(null);
    const [userMessage, setUserMessage] = useState("");

    useEffect(() => {
        if (isEmpty(loggedInUser)) {
            return
        }
        setFormState(prev => ({
            ...prev,
            email: loggedInUser.email,
            first_name: loggedInUser.first_name,
            last_name: loggedInUser.last_name,
        }));
    }, [loggedInUser]);

    useEffect(() => {
        setFormState(prev => ({
            ...prev,
            accepted_tos: !isEmpty(userProfile) && userProfile.has_accepted_tos,
        }));
    }, [userProfile]);

    useEffect(() => {
        console.log("Selected organization changed: ", selectedOrganization);
        setFormState(prev => ({
            ...prev,
            organization_id: selectedOrganization?.id,
            organization_name: selectedOrganization?.name
        }));
    }, [selectedOrganization]);

    useEffect(() => {
        if (formState.integration) {
            setFormState(prev => ({
                ...prev,
                ...(formState.integration === METRC ? getMetrcStripeIds() : getDutchieStripeIds()),
            }));
        }
        if (isEmpty(selectedIntegration) || formState?.integration) {
            return;
        }
        setFormState(prev => ({
            ...prev,
            integration: selectedIntegration,
            ...(selectedIntegration === METRC ? getMetrcStripeIds() : getDutchieStripeIds()),
        }));
    }, [selectedIntegration]);

    useEffect(() => {
        if (isEmpty(selectedIntegrationAccounts)) {
            return;
        }
        if (formState.integration === METRC && selectedIntegrationAccounts[0]?.token) {
            // Metrc integrations have user_key, dutchie has token.
            return;
        }
        if (formState.integration === DUTCHIE && selectedIntegrationAccounts[0]?.user_key) {
            // Metrc integrations have user_key, dutchie has token.
            return
        }

        if (formState.integration === DUTCHIE) {
            setFormState(prev => ({
                ...prev,
                api_key: prev.api_key || selectedIntegrationAccounts[0].token,
                confirm_api_key: prev.confirm_api_key || selectedIntegrationAccounts[0].token,
                location_name: prev.location_name || selectedIntegrationAccounts[0].display_name,
                ...getDutchieStripeIds()
            }));
        } else if (formState.integration === METRC) {
            setFormState(prev => ({
                ...prev,
                api_key: prev.api_key || selectedIntegrationAccounts[0].user_key,
                confirm_api_key: prev.confirm_api_key || selectedIntegrationAccounts[0].user_key,
                state_id: prev.state_id || selectedIntegrationAccounts[0].state_id,
                ...getMetrcStripeIds()
            }));
        }

        setFormState(prev => ({
            ...prev,
            store_count: prev.store_count || selectedIntegrationAccounts.length,
        }));

    }, [selectedIntegrationAccounts]);

    useEffect(() => {
        if (userOverride) {
            return;
        }
        const completed = getCompletedSteps({
            loggedInUser, userProfile, selectedOrganization,
            organizations, integrationAccounts, selectedIntegration,
            formState, selectedIntegrationAccounts
        })
        for (let step=0; step<STEPS.length - 1; step++) {
            if (!completed.find(s => s === STEPS[step])) {
                setActiveStep(step);
                return;
            }
        }
    }, [
        formState,
        activeStep
    ]);

    const stopLoading = () => {
        setShowSpinner(false);
        setCreatingOrganization(false);
        setCreatingUser(false);
        setCreatingOrganization(false);
        setConfiguringUsers(false);
        setLoggingIn(false);
        setCreatingIntegration(false);
    };

    const acceptTermsOfService = () => {
        return dispatch(updateUserProfile({id: userProfile.id, has_accepted_tos: true})).then((updatedProfile) => {
            dispatch(enqueueSnackbar({
                message: "Accepted Terms of Service.",
                options: {
                    variant: "success"
                }
            }))
            return false;
        }).catch(err => {
            console.error("Error updating user profile: ", err);
            stopLoading();
            if ("status" in err) {
                delete err.status;
            }
            setErrors({
                ...errors,
                ...err
            });
            dispatch(enqueueSnackbar({
                message: "Failed to accept terms of service.",
                options: {
                    variant: "error",
                }
            }));
            return true;
        });
    }

    const registerUser = () => {
        if (loggedInUser?.id && userProfile?.email_is_verified) {
            console.warn("User is already logged in, skipping registration step.");
            return Promise.resolve(false);
        }
        if (!validateRegister(loggedInUser, userProfile, formState, setErrors, errors, defaultErrors.current)) {
            console.trace();
            console.error("Failed to validate registration form.");
            return Promise.resolve(false);
        }
        if (isEmpty(loggedInUser)) {
            try {Tracker.trackFormSubmission({formName: "Register"});} catch (e) {}
            setShowSpinner(true);
            setCreatingUser(true);
            return dispatch(createUser({
                email: formState.email,
                username: formState.email,
                first_name: "",
                last_name: "",
                password: ""
            }, formState)).then((successMessage) => {
                setLoggingIn(true);
                setVerifyLink(successMessage.link);
                setLoggingIn(false);
                setCreatingUser(false);
                setShowSpinner(false);
                setUserMessage("We've successfully registered you, just check your email and click the link we sent to verify your address.")
            }).catch(err => {
                console.error("Error creating user: ", err);
                stopLoading();
                if ("status" in err) {
                    delete err.status;
                }
                setErrors({
                    ...errors,
                    ...err
                });
                return Promise.resolve(true);
            });
        }
    }

    const _createOrganization = () => {
        if (!validateOrganization(formState, setErrors, errors)) {
            console.log("Organization did not validate");
            return;
        }
        if (isEmpty(selectedOrganization) && !isEmpty(organizations) && formState?.organization_name) {
            const existingOrganization = organizations.find(org => org.name === formState.organization_name);
            if (existingOrganization) {
                console.log("Found existing organization: ", existingOrganization);
                dispatch(selectOrganization(existingOrganization));
                setFormState(prev => ({
                    ...prev,
                    organization_id: existingOrganization.id,
                    organization_name: existingOrganization.name
                }))
                return;
            }
        }
        if (organizations.find(o => o.name?.toLowerCase().trim() === formState.organization_name?.toLowerCase().trim())) {
            console.log("Organization already exists, skipping creation.");
            return Promise.resolve(false);
        }

        setCreatingOrganization(true);
        setShowSpinner(true);

        if (!formState.organization_id) {
            console.log("Creating organization: ", formState.organization_name);
            return dispatch(createOrganization(formState.organization_name, loggedInUser)).then(createdOrganization => {
                console.log("Created organization: ", createdOrganization);
                dispatch(selectOrganization(createdOrganization));
                setFormState(prev => ({
                    ...prev,
                    organization_id: createdOrganization.id,
                    organization_name: createdOrganization.name
                }))
                fetchUserMetadata({
                    user: loggedInUser,
                    onSend: () => {
                        setCreatingOrganization(false);
                        setConfiguringUsers(true);
                        setShowSpinner(true);
                    },
                    onComplete: (user, fetchedUserProfile, fetchedOrganizationUsers, fetchedOrganizations, fetchedOrganizationUserGroups, fetchedIntegrationAccounts) => {
                        setUserProfile(fetchedUserProfile);
                        setIntegrationAccounts(fetchedIntegrationAccounts);
                        dispatch(selectIntegrationAccounts(fetchedIntegrationAccounts));
                        setOrganizations(fetchedOrganizations);
                        dispatch(userLoggedIn({
                            user: user,
                            profile: fetchedUserProfile,
                            organizationUsers: fetchedOrganizationUsers,
                            organizations: fetchedOrganizations,
                            organizationUserGroups: fetchedOrganizationUserGroups,
                        }));
                        stopLoading();
                    },
                    onError: () => {},
                    dispatch
                });
                console.log("Finished creating organization and reloading metadata.");
                return Promise.resolve(false);
            }).catch(err => {
                console.error("Error creating organization: ", err);
                stopLoading();
                if ("status" in err) {
                    delete err.status;
                }
                setErrors({
                    ...errors,
                    ...err
                });
                return true;
            });
        } else {
            const org = organizations.find(o => o.id === formState.organization_id);
            if (org) {
                console.log("Selecting existing organization: ", org);
                dispatch(selectOrganization(org));
                setFormState(prev => ({...prev, organization_id: org.id, organization_name: org.name}))
            }
            console.log("Organization already exists, skipping creation.");
            setCreatingOrganization(false);
            return Promise.resolve(false);
        }
    }

    const setUpKeys = () => {
        setIntegrationAccounts(selectedIntegrationAccounts);
        return Promise.resolve(false);
    }

    const handleNext = (evt, step) => {
        setUserOverride(true);
        setErrors(defaultErrors.current);

        console.log("Handling next")

        if (step === STEPS.length - 1) return;

        let executeStep = Promise.resolve(false);
        if (step === STEPS.indexOf(TOS)) {
            console.log("Executing acceptTermsOfService");
            executeStep = acceptTermsOfService();
        } else if (step === STEPS.indexOf(REGISTER)) {
            console.log("Executing registerUser");
            executeStep = registerUser();
        } else if (step === STEPS.indexOf(CREATE_AN_ORGANIZATION)) {
            console.log("Executing createOrganization");
            executeStep = _createOrganization();
        } else if (step === STEPS.indexOf(SET_UP_KEYS) && !isEmpty(selectedIntegrationAccounts)) {
            console.log("Executing setUpKeys");
            executeStep = setUpKeys();
        } else {
            console.log("Other step: ", STEPS[step]);
        }

        executeStep?.then((stepErrored) => {
            console.log("Step errored?", stepErrored);
            const target = step + 1;
            const completed = getCompletedSteps({
                loggedInUser,
                userProfile,
                formState,
                selectedIntegrationAccounts
            })
            console.log("Completed steps: ", completed, "Current step: ", STEPS[step], "Target step: ", STEPS[target], "formState", formState);
            if (completed.find(s => s === STEPS[step])) {
                console.log("Step completed, moving to next step: ", STEPS[target]);
                stepErrored || setActiveStep(target);
            }
        });
    };

    const handleBack = () => {
        setUserOverride(true);
        if (activeStep === 0) {
            return;
        }
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const disableNext = (activeStep, loggedInUser, userProfile, organizations, integrationAccounts) => {
        const completed = getCompletedSteps({
            activeStep,
            loggedInUser,
            userProfile,
            organizations,
            integrationAccounts,
            selectedOrganization,
            selectedIntegration,
            formState,
            selectedIntegrationAccounts
        })
        const current = STEPS[activeStep];
        if (completed.find(step => step === current)) {
            return false;
        }
        if (activeStep === STEPS.length - 1) {
            return true;
        }
        return formIncomplete(loggedInUser, formState, activeStep, integrationAccounts);
    };

    if (hideModal) {
        return null;
    }

    return <Modal
        open={!hideModal}
        onClose={() => setHideModal(true)}
        className={"create-paid-account"}>
        <div className={"create-paid-account-container"}>
            {verifyLink && <input id={"verification-link"} type={"hidden"} value={verifyLink}/>}
            {isVertical ? null : <div className={"create-paid-account-header"}>
                {/*<img src={process.env.REACT_APP_PUBLIC_URI + "/canold-logo.webp"} alt={"Canold Logo"} className={"create-paid-account-logo"}/>*/}
                <CreatePaidAccountStepper activeStep={activeStep} orientation={"horizontal"}/>
            </div>}
            <div className={"create-paid-account-stepper-content" + (isVertical ? " vertical" : " horizontal") + (isMobile ? " mobile" : "")}>
                <div className={"create-paid-account-stepper-left"}>
                    {isVertical ? <>
                        <img src={process.env.REACT_APP_PUBLIC_URI + "/canold-logo.webp"} alt={"Canold Logo"} className={"create-paid-account-logo"}/>
                        <CreatePaidAccountStepper activeStep={activeStep} orientation={"vertical"}/>
                    </>: null}
                </div>
                <div className={"create-paid-account-stepper-right" + (isVertical ? " vertical" : " horizontal")}>
                    <StepComponent
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                        setFormState={setFormState}
                        formState={formState}
                        setErrors={setErrors}
                        errors={errors}
                        creatingAccount={creatingAccount}
                        setCreatingAccount={setCreatingAccount}
                        loggedInUser={loggedInUser}
                        userProfile={userProfile}
                        organizations={organizations}
                        integrationAccounts={integrationAccounts}
                        allowBackNavigation={allowBackNavigation}
                        setAllowBackNavigation={setAllowBackNavigation}
                        selectedIntegrationAccounts={selectedIntegrationAccounts}
                        setIntegrationAccounts={setIntegrationAccounts}
                        setUserOverride={setUserOverride}
                    />
                </div>
            </div>
            <div className={"create-paid-account-status"}>
                {showSpinner && <CreatingAccountSpinner
                    formState={formState}
                    creatingUser={creatingUser}
                    creatingOrganization={creatingOrganization}
                    configuringUsers={configuringUsers}
                    loggingIn={loggingIn}
                    creatingIntegration={creatingIntegration}
                />}
                {userMessage}
            </div>
            <div className={"create-paid-account-actions" + (isVertical ? " vertical" : " horizontal")}>
                <Button
                    variant="outlined"
                    onClick={() => setHideModal(true)}
                    id={"create-paid-account-close-button"}
                    className={"create-paid-account-close-button"}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    onClick={(evt) => {
                        console.log("Event", evt, "Active Step", activeStep);
                        handleNext(evt, activeStep)
                    }}
                    className={"create-paid-account-next-button"}
                    id={"create-paid-account-next-button"}
                    disabled={disableNext(activeStep, loggedInUser, userProfile, organizations, integrationAccounts)}
                >
                    {activeStep === STEPS.length - 1 ? "Finish" : "Next"}
                </Button>
                <Button
                    variant={"outlined"}
                    disabled={activeStep === 0}
                    id={"create-paid-account-back-button"}
                    onClick={handleBack}
                    className={"create-paid-account-back-button"}
                >
                    Back
                </Button>
            </div>
        </div>
    </Modal>


};

export default CreatePaidAccount;